import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Pencil,
  Archive,
  ArrowRight,
  Link,
  X,
  ChevronDown,
  CalendarClock,
  Check,
} from 'lucide-react';
import type { KanbanColumn } from 'types/kanban';
import styles from './CardMenu.module.scss';

export type CardMenuView = 'main' | 'moveToList';

export interface CardMenuAnchor {
  cardId: string;
  currentColumnId: string;
  rect: DOMRect;
  boundsRect?: DOMRect | null;
}

export interface CardMenuProps {
  anchor: CardMenuAnchor | null;
  activeColumnId?: string | null;
  closeOnBackFromMoveView?: boolean;
  activeBoardId?: string | null;
  boards?: Array<{ id: string; title: string }>;
  getBoardListsForMove?: (boardId: string) => Promise<KanbanColumn[]>;
  view: CardMenuView;
  columns: KanbanColumn[];
  onClose: () => void;
  onViewChange: (view: CardMenuView) => void;
  onRenameCard: (cardId: string) => void;
  onToggleStartDatePanel?: (cardId: string) => void;
  isStartDatePanelOpen?: boolean;
  onToggleDueDatePanel?: (cardId: string) => void;
  isDueDatePanelOpen?: boolean;
  onCopyCardLink: (cardId: string) => void | Promise<void>;
  onArchiveCard: (cardId: string) => void;
  onMoveCard: (
    cardId: string,
    targetColumnId: string,
    targetBoardId?: string,
    targetPosition0Based?: number,
  ) => void | Promise<void>;
}

export default function CardMenu({
  anchor,
  activeColumnId = null,
  closeOnBackFromMoveView = false,
  activeBoardId = null,
  boards = [],
  getBoardListsForMove,
  view,
  columns,
  onClose,
  onViewChange,
  onRenameCard,
  onToggleStartDatePanel = undefined,
  isStartDatePanelOpen = false,
  onToggleDueDatePanel = undefined,
  isDueDatePanelOpen = false,
  onCopyCardLink,
  onArchiveCard,
  onMoveCard,
}: CardMenuProps) {
  const anchorCurrentColumnId = anchor?.currentColumnId ?? '';
  const effectiveCurrentColumnId = activeColumnId ?? anchorCurrentColumnId;
  const effectiveCurrentBoardId = activeBoardId ?? null;
  const boardOptions = useMemo(
    () =>
      boards.length > 0
        ? boards
        : effectiveCurrentBoardId
          ? [{ id: effectiveCurrentBoardId, title: 'Current board' }]
          : [],
    [boards, effectiveCurrentBoardId],
  );
  const [selectedBoardId, setSelectedBoardId] = useState<string>(
    effectiveCurrentBoardId ?? boardOptions[0]?.id ?? '',
  );
  const [boardDropdownOpen, setBoardDropdownOpen] = useState(false);
  const [moveTargets, setMoveTargets] = useState<KanbanColumn[]>(columns);
  const [targetColumnId, setTargetColumnId] = useState<string>(
    effectiveCurrentColumnId || moveTargets[0]?.id || '',
  );
  const destTargetCol = moveTargets.find((c) => c.id === targetColumnId);
  const destN = destTargetCol?.cardIds?.length ?? 0;
  const [targetPosition1Based, setTargetPosition1Based] = useState<number>(1);
  const [listDropdownOpen, setListDropdownOpen] = useState(false);
  const [positionDropdownOpen, setPositionDropdownOpen] = useState(false);

  useEffect(() => {
    setSelectedBoardId(effectiveCurrentBoardId ?? boardOptions[0]?.id ?? '');
  }, [effectiveCurrentBoardId, boardOptions]);

  useEffect(() => {
    if (view !== 'moveToList') setBoardDropdownOpen(false);
  }, [view]);

  useEffect(() => {
    let cancelled = false;
    const loadTargets = async () => {
      if (!selectedBoardId) {
        if (!cancelled) setMoveTargets([]);
        return;
      }
      if (selectedBoardId === effectiveCurrentBoardId) {
        if (!cancelled) setMoveTargets(columns);
        return;
      }
      if (!getBoardListsForMove) {
        if (!cancelled) setMoveTargets([]);
        return;
      }
      try {
        const lists = await getBoardListsForMove(selectedBoardId);
        if (!cancelled) setMoveTargets(lists);
      } catch {
        if (!cancelled) setMoveTargets([]);
      }
    };
    void loadTargets();
    return () => {
      cancelled = true;
    };
  }, [selectedBoardId, effectiveCurrentBoardId, columns, getBoardListsForMove]);

  useEffect(() => {
    const defaultListId =
      selectedBoardId === effectiveCurrentBoardId
        ? effectiveCurrentColumnId || moveTargets[0]?.id || ''
        : moveTargets[0]?.id || '';
    setTargetColumnId(defaultListId);
  }, [
    effectiveCurrentColumnId,
    moveTargets,
    selectedBoardId,
    effectiveCurrentBoardId,
  ]);

  useEffect(() => {
    // Reset position to top whenever destination list changes.
    setTargetPosition1Based(1);
    setPositionDropdownOpen(false);
  }, [targetColumnId]);

  useEffect(() => {
    if (view !== 'moveToList') {
      setListDropdownOpen(false);
      setBoardDropdownOpen(false);
      setPositionDropdownOpen(false);
    }
  }, [view]);

  if (!anchor) return null;

  const isMobile = window.innerWidth <= 480;
  const sidePadding = 12;
  const menuWidth = view === 'moveToList' ? 320 : 220;
  const menuHeightEstimate = view === 'moveToList' ? 340 : 220;
  const fallbackBounds = {
    left: sidePadding,
    right: window.innerWidth - sidePadding,
    top: 8,
    bottom: window.innerHeight - 8,
  };
  const bounds = anchor.boundsRect ?? fallbackBounds;
  const width = isMobile
    ? Math.min(menuWidth, Math.max(160, bounds.right - bounds.left - 16))
    : menuWidth;
  const minLeft = bounds.left + 8;
  const maxLeft = bounds.right - width - 8;
  const minTop = bounds.top + 8;
  const maxTop = bounds.bottom - menuHeightEstimate - 8;
  const preferredLeft = anchor.rect.right - width;
  const preferredTop = anchor.rect.bottom + 4;
  const fallbackTop = anchor.rect.top - menuHeightEstimate - 4;
  const resolvedLeft = Math.min(Math.max(preferredLeft, minLeft), maxLeft);
  const resolvedTop =
    preferredTop + menuHeightEstimate <= bounds.bottom - 8
      ? preferredTop
      : Math.max(minTop, Math.min(fallbackTop, maxTop));

  const style: React.CSSProperties = {
    position: 'fixed',
    top: resolvedTop,
    left: resolvedLeft,
    width,
  };

  return createPortal(
    <>
      <div className={styles.cardMenuBackdrop} onClick={onClose} aria-hidden />
      <div
        className={`${styles.cardMenu} ${view === 'moveToList' ? styles.cardMenuMove : ''}`}
        style={style}
        role="menu"
      >
        {view === 'main' ? (
          <>
            <button
              type="button"
              className={styles.cardMenuItem}
              onClick={() => {
                void onCopyCardLink(anchor.cardId);
                onClose();
              }}
              role="menuitem"
            >
              <Link size={14} />
              Copy card link
            </button>
            <button
              type="button"
              className={styles.cardMenuItem}
              onClick={() => {
                onRenameCard(anchor.cardId);
                onClose();
              }}
              role="menuitem"
            >
              <Pencil size={14} />
              Rename
            </button>
            {onToggleStartDatePanel ? (
              <button
                type="button"
                className={styles.cardMenuItem}
                onClick={() => {
                  onToggleStartDatePanel?.(anchor.cardId);
                  onClose();
                }}
                role="menuitem"
              >
                <CalendarClock size={14} />
                Start Date
                {isStartDatePanelOpen ? (
                  <Check
                    size={14}
                    className={styles.cardMenuItemTrailingIcon}
                  />
                ) : null}
              </button>
            ) : null}
            {onToggleDueDatePanel ? (
              <button
                type="button"
                className={styles.cardMenuItem}
                onClick={() => {
                  onToggleDueDatePanel(anchor.cardId);
                  onClose();
                }}
                role="menuitem"
              >
                <CalendarClock size={14} />
                Due Date
                {isDueDatePanelOpen ? (
                  <Check
                    size={14}
                    className={styles.cardMenuItemTrailingIcon}
                  />
                ) : null}
              </button>
            ) : null}
            <button
              type="button"
              className={styles.cardMenuItem}
              onClick={() => {
                onArchiveCard(anchor.cardId);
                onClose();
              }}
              role="menuitem"
            >
              <Archive size={14} />
              Archive card
            </button>
            <button
              type="button"
              className={styles.cardMenuItem}
              onClick={() => onViewChange('moveToList')}
              role="menuitem"
            >
              <ArrowRight size={14} />
              Move to list
            </button>
          </>
        ) : (
          <div className={styles.moveCardPanel}>
            <div className={styles.moveCardHeader}>
              <div className={styles.moveCardTitle}>Move card</div>
              <button
                type="button"
                className={styles.moveCardCloseBtn}
                onClick={onClose}
                aria-label="Close move card panel"
              >
                <X size={14} />
              </button>
            </div>
            <div className={styles.moveFormGroup}>
              <label className={styles.moveFormLabel}>Board</label>
              <div className={styles.moveDropdown}>
                <button
                  type="button"
                  className={styles.moveDropdownBtn}
                  onClick={() => {
                    setBoardDropdownOpen((prev) => !prev);
                    setListDropdownOpen(false);
                    setPositionDropdownOpen(false);
                  }}
                  aria-label="Board"
                  aria-expanded={boardDropdownOpen}
                >
                  <span>
                    {boardOptions.find((b) => b.id === selectedBoardId)
                      ?.title ?? 'Select board'}
                  </span>
                  <ChevronDown size={14} />
                </button>
                {boardDropdownOpen && (
                  <div className={styles.moveDropdownOptions} role="listbox">
                    {boardOptions.map((board) => (
                      <button
                        key={board.id}
                        type="button"
                        className={styles.moveDropdownOption}
                        onClick={() => {
                          setSelectedBoardId(board.id);
                          setBoardDropdownOpen(false);
                          setListDropdownOpen(false);
                        }}
                        role="option"
                        aria-selected={selectedBoardId === board.id}
                      >
                        {board.title}
                        {board.id === effectiveCurrentBoardId
                          ? ' (current)'
                          : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.moveRow}>
              <div className={styles.moveFormGroup}>
                <label className={styles.moveFormLabel}>List</label>
                <div className={styles.moveDropdown}>
                  <button
                    type="button"
                    className={styles.moveDropdownBtn}
                    onClick={() => {
                      setListDropdownOpen((prev) => !prev);
                      setPositionDropdownOpen(false);
                    }}
                    aria-label="List"
                    aria-expanded={listDropdownOpen}
                  >
                    <span>
                      {moveTargets.find((col) => col.id === targetColumnId)
                        ?.title ?? 'Select list'}
                    </span>
                    <ChevronDown size={14} />
                  </button>
                  {listDropdownOpen && (
                    <div className={styles.moveDropdownOptions} role="listbox">
                      {moveTargets.map((col) => (
                        <button
                          key={col.id}
                          type="button"
                          className={styles.moveDropdownOption}
                          onClick={() => {
                            setTargetColumnId(col.id);
                            setListDropdownOpen(false);
                          }}
                          role="option"
                          aria-selected={targetColumnId === col.id}
                        >
                          {col.title}
                          {selectedBoardId === effectiveCurrentBoardId &&
                          col.id === effectiveCurrentColumnId
                            ? ' (current)'
                            : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.moveFormGroup}>
                <label className={styles.moveFormLabel}>Position</label>
                <div className={styles.moveDropdown}>
                  <button
                    type="button"
                    className={styles.moveDropdownBtn}
                    onClick={() => {
                      setPositionDropdownOpen((prev) => !prev);
                      setBoardDropdownOpen(false);
                      setListDropdownOpen(false);
                    }}
                    aria-label="Position"
                    aria-expanded={positionDropdownOpen}
                  >
                    <span>{targetPosition1Based}</span>
                    <ChevronDown size={14} />
                  </button>
                  {positionDropdownOpen && (
                    <div className={styles.moveDropdownOptions} role="listbox">
                      {Array.from({ length: destN + 1 }, (_, i) => i + 1).map(
                        (pos) => (
                          <button
                            key={pos}
                            type="button"
                            className={styles.moveDropdownOption}
                            onClick={() => {
                              setTargetPosition1Based(pos);
                              setPositionDropdownOpen(false);
                            }}
                            role="option"
                            aria-selected={targetPosition1Based === pos}
                          >
                            {pos}
                          </button>
                        ),
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.moveActions}>
              <button
                type="button"
                className={`${styles.cardBtn} ${styles.cardBtnSecondary}`}
                onClick={() => {
                  if (closeOnBackFromMoveView) {
                    onClose();
                    return;
                  }
                  onViewChange('main');
                }}
              >
                Back
              </button>
              <button
                type="button"
                className={`${styles.cardBtn} ${styles.cardBtnPrimary}`}
                onClick={() => {
                  if (!targetColumnId) return;
                  void onMoveCard(
                    anchor.cardId,
                    targetColumnId,
                    selectedBoardId,
                    Math.max(0, targetPosition1Based - 1),
                  );
                  onClose();
                }}
                disabled={!targetColumnId}
              >
                Move
              </button>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
